import type { ApiJobSynonymId } from '#/api/types/ApiTypedId';
import { Button } from '#/components/Button/Button';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SalariesJobTitle } from '#/modules/salary/types/SalariesJobTitle';
import { colors, fontWeights } from '#/theme/theme';

import { Card } from './Card';
import { Container } from './Container';
import { BigButton } from './JobSelector.styled';
import { NoJobsMatch_Box_1 } from './NoJobsMatch.styled';

type Props = {
  suggestion?: { name: string; jobTitle: SalariesJobTitle; objectID: string };
  onSelect: (arg0: {
    name: string;
    familyId: string;
    id: string;
    synonymId: ApiJobSynonymId;
  }) => void;
  title: string;
  onContinue?: () => void;
};

export function NoJobsMatch({
  suggestion,
  onSelect,
  title,
  onContinue,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Container>
      <Card
        content={{
          name: (
            <>
              {i18n('We don’t currently have a match for this job title')}
              &hellip;
            </>
          ),

          body:
            suggestion &&
            /* TRANSLATORS: salary survey, continue with free text job title */
            i18n('Did you mean {{link}}%(title)s{{/link}}?', {
              title: suggestion.name,
              link: (
                <Button
                  type="button"
                  variant="tertiary"
                  qaId="suggestion-link"
                  onClick={() =>
                    onSelect({
                      name: suggestion.name,
                      id: suggestion.jobTitle.id,
                      familyId: suggestion.jobTitle.familyId,
                      synonymId: suggestion.objectID,
                    })
                  }
                />
              ),
            }),
          description: suggestion
            ? undefined
            : i18n('Try searching for a related or more general title'),
        }}
      />

      {onContinue && (
        <BigButton
          type="button"
          data-qa-id="continue-link"
          onClick={onContinue}
        >
          <NoJobsMatch_Box_1>
            {
              /* TRANSLATORS: salary survey, continue with free text job title */
              i18n('Continue with {{link}}%(title)s{{/link}}', {
                title,
                link: (
                  <span
                    style={{
                      fontWeight: fontWeights.semiBold,
                      color: colors.brandBlue,
                    }}
                  />
                ),
              })
            }
          </NoJobsMatch_Box_1>
        </BigButton>
      )}
    </Container>
  );
}
